import { useCallback, useEffect, useState } from "react";
import { Center, Loader, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { LineChart } from "@mantine/charts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import "@mantine/charts/styles.css";

dayjs.extend(utc);

const RANGE_OPTIONS = [
  { label: "30 days", value: "30" },
  { label: "90 days", value: "90" },
  { label: "365 days", value: "365" },
  { label: "2 years", value: "730" },
  { label: "All time", value: "all" },
] as const;

export default function AdminCompanyOnboardingChartPage() {
  const [range, setRange] = useState<string>("90");
  const [series, setSeries] = useState<{ date: string; count: number }[]>([]);
  const [resolvedDays, setResolvedDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const allTime = range === "all";
      const days = allTime ? 90 : Number(range);
      const res = await trpc.admin.companyOnboardByDay.query(allTime ? { allTime: true } : { days });
      setSeries(res.series);
      setResolvedDays(res.days ?? days);
    } catch (e) {
      handleTrpcError(e);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const tickInterval = Math.max(0, Math.floor(resolvedDays / 12) - 1);

  const formatXTick = useCallback((value: string) => {
    const d = dayjs.utc(value);
    if (!d.isValid()) return String(value);
    return resolvedDays > 400 ? d.format("MMM D 'YY") : d.format("MMM D");
  }, [resolvedDays]);

  return (
    <Stack gap="lg" p="md">
      <div>
        <Title order={2}>Company onboarding</Title>
        <Text c="dimmed" size="sm" mt={4}>
          New companies per UTC day (Company.createdAt). All statuses included.
        </Text>
      </div>

      <SegmentedControl
        value={range}
        onChange={setRange}
        data={RANGE_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
      />

      {loading ? (
        <Center h={360}>
          <Loader />
        </Center>
      ) : series.length === 0 ? (
        <Text c="dimmed">No data in this range.</Text>
      ) : (
        <LineChart
          h={380}
          data={series}
          dataKey="date"
          series={[{ name: "count", label: "Companies", color: "blue.6" }]}
          curveType="monotone"
          strokeWidth={2}
          gridAxis="xy"
          xAxisLabel="Date (UTC)"
          yAxisLabel="Count"
          valueFormatter={(v) => String(Math.round(v))}
          xAxisProps={{
            tickFormatter: formatXTick,
            interval: tickInterval,
            angle: -35,
            textAnchor: "end",
            height: 56,
          }}
          tooltipProps={{
            labelFormatter: (_label, payload) => {
              const row = payload?.[0]?.payload as { date?: string } | undefined;
              if (row?.date) {
                return dayjs.utc(row.date).format("YYYY-MM-DD (UTC)");
              }
              return "";
            },
          }}
        />
      )}
    </Stack>
  );
}
